import React, { Suspense, lazy, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { Analytics } from "@vercel/analytics/react";
import { injectSpeedInsights } from "@vercel/speed-insights";
import Navigation from "./components/Navigation";
import ErrorBoundary from "./components/ErrorBoundary";
import { useStore } from "./store/useStore";
import api from "./services/api";

const TimerPage = lazy(() => import("./pages/TimerPage"));
const InventoryPage = lazy(() => import("./pages/InventoryPage"));
const CraftingTrackerPage = lazy(() => import("./pages/CraftingTrackerPage"));
const TrainPage = lazy(() => import("./pages/TrainPage"));
const PuffyTrainsPage = lazy(() => import("./pages/PuffyTrainsPage"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));

function App() {
  const { language } = useStore();
  const isProd = import.meta.env.PROD;

  useEffect(() => {
    if (isProd) {
      injectSpeedInsights();
    }
  }, [isProd]);

  useEffect(() => {
    api.setLanguage(language);
  }, [language]);

  return (
    <ErrorBoundary
      title="Application error"
      fallbackMessage="The app ran into an unexpected issue. You can retry or reload."
    >
      <Router
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <div className="min-h-screen bg-gw2-dark text-white">
          <Navigation />
          <Suspense
            fallback={
              <div className="px-4 py-6 text-white/80">Loading page…</div>
            }
          >
            <Routes>
              <Route
                path="/"
                element={
                  <ErrorBoundary
                    title="Timer page error"
                    fallbackMessage="The timer page failed to render. Try again."
                  >
                    <TimerPage />
                  </ErrorBoundary>
                }
              />
              <Route
                path="/inventory"
                element={
                  <ErrorBoundary
                    title="Inventory page error"
                    fallbackMessage="The inventory page failed to render. Try again."
                  >
                    <InventoryPage />
                  </ErrorBoundary>
                }
              />
              <Route
                path="/crafting"
                element={
                  <ErrorBoundary
                    title="Crafting page error"
                    fallbackMessage="The crafting page failed to render. Try again."
                  >
                    <CraftingTrackerPage />
                  </ErrorBoundary>
                }
              />
              <Route
                path="/train"
                element={
                  <ErrorBoundary
                    title="Train page error"
                    fallbackMessage="The train page failed to render. Try again."
                  >
                    <TrainPage />
                  </ErrorBoundary>
                }
              />
              <Route
                path="/puffytrains"
                element={
                  <ErrorBoundary
                    title="Train builder error"
                    fallbackMessage="The train builder failed to render. Try again."
                  >
                    <PuffyTrainsPage />
                  </ErrorBoundary>
                }
              />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
          {isProd && <Analytics />}
        </div>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
